import { useMemo, useState } from "react"
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { ColumnDef } from "@tanstack/react-table"

import { ChipStyled } from "src/shared/components"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import { useIntegrationOperationsQuery } from "src/graphql/quark/quark.type"

type OperationRow = {
  id: string
  operationType: string
  oneObjectId: string
  externalObjectId?: string | null
  status: string
  attemptCount: number
  error?: string | null
  requestPayload?: unknown
  responsePayload?: unknown
  createdAt: string
  completedAt?: string | null
}

const STATUS_COLOR: Record<string, "success" | "error" | "warning" | "default"> = {
  SUCCEEDED: "success",
  FAILED_PERMANENT: "error",
  FAILED_RETRYABLE: "warning",
  IN_PROGRESS: "default",
  PENDING: "default",
  UNKNOWN_OUTCOME: "warning",
  CANCELLED: "default"
}

const ActivityTab = ({ connectionId }: { connectionId: string }) => {
  const [selected, setSelected] = useState<OperationRow | null>(null)

  const { data, loading } = useIntegrationOperationsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only",
    pollInterval: 15000
  })

  const rows: OperationRow[] = data?.fusionIntegrationOperations ?? []

  const columns: ColumnDef<OperationRow, any>[] = useMemo(
    () => [
      {
        header: "When",
        accessorKey: "createdAt",
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleString()
      },
      { header: "Type", accessorKey: "operationType" },
      { header: "Object", accessorKey: "oneObjectId" },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <ChipStyled
            size="small"
            variant="outlined"
            label={row.original.status}
            color={STATUS_COLOR[row.original.status] ?? "default"}
          />
        )
      },
      { header: "Attempts", accessorKey: "attemptCount" },
      {
        header: "Error",
        accessorKey: "error",
        cell: ({ row }) =>
          row.original.error ? (
            <Tooltip title={row.original.error}>
              <Typography
                variant="body2"
                color="error"
                sx={{
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {row.original.error}
              </Typography>
            </Tooltip>
          ) : (
            "—"
          )
      },
      {
        header: "",
        id: "details",
        cell: ({ row }) => (
          <Tooltip title="View payload">
            <IconButton size="small" onClick={() => setSelected(row.original)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    ],
    []
  )

  return (
    <>
      <DataGridMui
        id="integration-activity"
        data={rows}
        columns={columns}
        isLoading={loading}
        paginationMode="client"
        initialPageSize={1000}
        getRowId={(row: OperationRow) => row.id}
        hidePagination
        noDataComponent={<Box p={3}>No sync activity yet.</Box>}
      />

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selected?.operationType} — {selected?.oneObjectId}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Sent to provider
          </Typography>
          <Box
            component="pre"
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: "action.hover",
              borderRadius: 1,
              overflow: "auto",
              fontSize: 12
            }}
          >
            {JSON.stringify(selected?.requestPayload ?? null, null, 2)}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Provider response
          </Typography>
          <Box
            component="pre"
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1,
              overflow: "auto",
              fontSize: 12
            }}
          >
            {JSON.stringify(selected?.responsePayload ?? null, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ActivityTab
